import { LoggerService } from '@angular-ru/cdk/logger';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { IButtonContainer } from 'src/app/api/modules/core/dynamic/containers/IButtonContainer';
import { DynamicContentDirective } from 'src/app/modules/shared/directives/dynamic-content/dynamic-content.directive';
import { ButtonComponent } from '../../components/button/button.component';
import { ContainerFactoryArray } from '../container/container.factory';

/**
 * On phone-sized viewports the container always stacks a centered column of
 * fixed-size buttons. Matches the upper bound of the legacy flex-layout 'xs'
 * alias this component previously observed.
 */
const XS_QUERY = '(max-width: 599.98px)';

/**
 * Renders a server-configured array of buttons in a responsive layout.
 *
 * The configured `layout` is honored for one or two buttons on viewports
 * larger than phones; otherwise the buttons stack in a centered column of
 * fixed-size buttons. The configured `buttonSizing` is pushed down into each
 * created button.
 *
 * Unlike a Button Container Template, the buttons come from the config's
 * `componentArray` and are created dynamically.
 */
@Component({
  selector: 'app-button-container',
  templateUrl: './button-container.component.html',
  styleUrls: ['./button-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DynamicContentDirective],
})
export class ButtonContainerComponent {
  private readonly loggerService = inject(LoggerService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  /**
   * The anchor the buttons are dynamically created into.
   */
  private readonly contentContainer = viewChild(DynamicContentDirective);

  /**
   * The Config as provided to the element.
   *
   * Nothing renders until a truthy, valid config arrives.
   */
  readonly config = input<IButtonContainer | null>();

  /**
   * Is the viewport phone-sized?
   */
  private readonly isXs = toSignal(this.breakpointObserver.observe(XS_QUERY).pipe(map((state) => state.matches)), {
    initialValue: this.breakpointObserver.isMatched(XS_QUERY),
  });

  /**
   * The validated button configurations, or a warning when the config can't
   * be rendered (logged by an effect; the container then renders nothing).
   */
  private readonly viewConfig = computed(() => {
    const configuration = this.config();
    if (!configuration) {
      return undefined;
    }

    const componentArray = configuration.componentArray ?? [];
    if (componentArray.length === 0) {
      return { buttons: undefined, warning: 'You must supply at least one button to a button container.' };
    }
    if (componentArray.some((component) => component?.selector !== 'app-button')) {
      return { buttons: undefined, warning: 'All components in the component array must be of `"selector": "app-button"`.' };
    }

    return { buttons: componentArray, warning: undefined };
  });

  /**
   * The active layout: flex styles for the wrapper and the sizing pushed
   * down to the created buttons.
   */
  protected readonly layoutState = computed(() => {
    const view = this.viewConfig();
    if (!view?.buttons) {
      return undefined;
    }

    const configuration = this.config();
    const preferredLayout = configuration?.layout;
    const preferredSizing = configuration?.buttonSizing;
    const count = view.buttons.length;

    // a row layout is honored for one or two buttons on viewports larger
    // than phones
    if ((preferredLayout === 'row' || preferredLayout === 'row-reverse') && count < 3 && !this.isXs()) {
      return { direction: preferredLayout, justifyContent: 'space-between', alignItems: 'center', sizing: preferredSizing };
    }

    // a single stacked button keeps the configured sizing
    if (count === 1) {
      return { direction: 'column', justifyContent: 'space-around', alignItems: 'center', sizing: preferredSizing };
    }

    // multiple stacked buttons are always fixed-size
    return { direction: 'column', justifyContent: 'space-around', alignItems: 'center', sizing: 'fixedSize' as const };
  });

  /**
   * References to the dynamically created buttons, for sizing pushdown
   * and teardown.
   */
  private readonly componentRefs = signal<ComponentRef<unknown>[]>([]);

  constructor() {
    // surface configuration problems (once per config change)
    effect(() => {
      const warning = this.viewConfig()?.warning;
      if (warning) {
        this.loggerService.warn(`[ButtonContainer] - ${warning}`);
      }
    });

    // (re)create the buttons whenever the anchor or the configs change
    effect(() => {
      const container = this.contentContainer();
      const buttons = this.viewConfig()?.buttons;

      untracked(() => this.componentRefs()).forEach((ref) => ref.destroy());
      this.componentRefs.set(container && buttons ? ContainerFactoryArray(container.viewContainerRef, buttons) : []);
    });

    // push the active sizing down to every created button
    effect(() => {
      const sizing = this.layoutState()?.sizing;
      for (const ref of this.componentRefs()) {
        if (ref.instance instanceof ButtonComponent) {
          ref.instance.isFixedSize = sizing === 'fixedSize';
          ref.instance.isFullSize = sizing === 'fullSize';
        }
      }
    });

    inject(DestroyRef).onDestroy(() => {
      this.componentRefs().forEach((ref) => ref.destroy());
    });
  }
}
